// src/modules/assetManagement/components/ui.tsx
// Small shared UI pieces for the asset screens.
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import type { AssetStatus, StorageState } from "../types";
import { STATUS_META, STORAGE_META } from "../utils";

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md";
  loading?: boolean;
  icon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "secondary",
  size = "md",
  loading,
  icon,
  children,
  className = "",
  style,
  disabled,
  ...rest
}) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-400 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";
  const sizes = size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm";
  const variants: Record<ButtonVariant, string> = {
    primary: "text-white shadow-sm hover:opacity-90",
    secondary: "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50",
    danger: "bg-red-600 text-white shadow-sm hover:bg-red-700",
    ghost: "text-slate-600 hover:bg-slate-100",
  };
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`${base} ${sizes} ${variants[variant]} ${className}`}
      style={
        variant === "primary"
          ? { backgroundColor: "var(--color-primary, #2563EB)", ...style }
          : style
      }
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : icon}
      {children}
    </button>
  );
};

export const StatusBadge: React.FC<{ status: AssetStatus }> = ({ status }) => {
  const meta = STATUS_META[status];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${meta.className}`}>
      {meta.label}
    </span>
  );
};

export const StorageBadge: React.FC<{ storage: StorageState; hideActive?: boolean }> = ({
  storage,
  hideActive,
}) => {
  if (hideActive && storage === "standard") return null;
  const meta = STORAGE_META[storage];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${meta.className}`}>
      {meta.label}
    </span>
  );
};

export const Chip: React.FC<{ className?: string; children: React.ReactNode; onRemove?: () => void }> = ({
  className = "bg-slate-100 text-slate-700 border-slate-200",
  children,
  onRemove,
}) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${className}`}>
    {children}
    {onRemove && (
      <button type="button" onClick={onRemove} className="opacity-60 hover:opacity-100" aria-label="Remove">
        <X size={12} />
      </button>
    )}
  </span>
);

export const SearchInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, placeholder, className = "w-64" }) => (
  <div className={`relative ${className}`}>
    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-9 pr-8 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400"
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange("")}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        aria-label="Clear search"
      >
        <X size={14} />
      </button>
    )}
  </div>
);

export const Select: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  icon?: React.ReactNode;
  className?: string;
  ariaLabel: string;
}> = ({ value, onChange, options, icon, className = "w-44", ariaLabel }) => (
  <div className={`relative ${className}`}>
    {icon && (
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">{icon}</span>
    )}
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full appearance-none ${icon ? "pl-9" : "pl-3"} pr-8 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-200 truncate ${
        value !== "all" ? "border-slate-500 text-slate-900 font-medium" : "border-slate-300 text-slate-700"
      }`}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs">▾</span>
  </div>
);

export const Modal: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: string;
}> = ({ title, subtitle, onClose, children, footer, width = "max-w-lg" }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]`}
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            {subtitle && <div className="text-sm text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 -m-1" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const EmptyState: React.FC<{ icon: React.ReactNode; title: string; hint?: string; action?: React.ReactNode }> = ({
  icon,
  title,
  hint,
  action,
}) => (
  <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300">
    <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3">
      {icon}
    </div>
    <p className="font-medium text-slate-700">{title}</p>
    {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export function useToast() {
  const [toast, setToast] = useState<{ message: string; tone: "success" | "error" } | null>(null);
  const clear = useCallback(() => setToast(null), []);
  const showToast = useCallback(
    (message: string, tone: "success" | "error" = "success") => setToast({ message, tone }),
    [],
  );
  const toastNode = <Toast message={toast?.message ?? null} tone={toast?.tone} onDone={clear} />;
  return { toastNode, showToast };
}

/** Lightweight toast without extra dependencies */
export const Toast: React.FC<{ message: string | null; tone?: "success" | "error"; onDone: () => void }> = ({
  message,
  tone = "success",
  onDone,
}) => {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [message, onDone]);
  if (!message) return null;
  return (
    <div
      role="status"
      className={`fixed bottom-6 right-6 z-[110] px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white ${
        tone === "success" ? "bg-slate-900" : "bg-red-600"
      }`}
    >
      {message}
    </div>
  );
};
